import React, { memo, useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Icon } from 'shared/ui';

import { useGetProjectsData } from './model/useGetData';
import cls from './ObjectsOrEstimates.module.css';
import { SearchSelectWithSubList } from './ui/SearchSelectWithSubList';
import { FieldErrors } from '../../entities/modal/model/types/modalFieldErrors';
import { Task } from '../../entities/task/model/types/tasks';
import { TModalMode } from '../../shared/model/types/modalMode.type';
import { useAppDispatch, useAppSelector } from '../../store';
import { TObjectOrEstimateOption } from './model/types';
import { changeEstimateId, changeProjectId } from '../../widgets/Modals/model/slice/addModalSlice';
import {
    changeEditModalEstimateId,
    changeEditModalProjectId,
} from '../../widgets/Modals/model/slice/editModalSlice';

interface Props {
    isDisabled: boolean;
    modalMode: TModalMode;
    error: FieldErrors['objectOrEstimate'];
    setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
}

export const ObjectsOrEstimatesSelect = memo(
    ({ isDisabled = false, modalMode, error, setFieldErrors }: Props) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const { projects } = useGetProjectsData();
        const pickedTaskUid = new URLSearchParams(window.location.search).get('task_id');
        const taskData: Task = useAppSelector(
            (state) => state.tasksReducer.entities[pickedTaskUid]
        );

        const [selectedOption, setSelectedOption] = useState(() => {
            if (modalMode === 'addNewTask') return [];

            const filteredProject =
                projects.filter((item) => item?.id === taskData?.project_id)[0] || {};
            const filteredEstimate =
                filteredProject?.estimates?.filter(
                    (item) => item?.id === taskData?.estimate_id
                )[0] || {};

            return {
                id: filteredProject.id || 0,
                name: filteredEstimate.name
                    ? `${filteredProject.name || ''} / ${filteredEstimate.name}`
                    : filteredProject.name || '',
                estimates: filteredProject.estimates
                    ? filteredProject.estimates.map((item) => item.id)
                    : [],
            };
        });

        const handleSelectChange = useCallback(
            (value: TObjectOrEstimateOption) => {
                // if (!value.id) return;
                if (!value.id) {
                    dispatch(changeEstimateId(null));
                    dispatch(changeProjectId(null));
                }

                setSelectedOption(value);
                if (value.type === 'estimate' && modalMode === 'addNewTask') {
                    dispatch(changeEstimateId(value.id));
                    dispatch(changeProjectId(value.projectId));
                }

                if (value.type === 'estimate' && modalMode === 'editTask') {
                    // setEstimateIdInEditMode(value.id);
                    dispatch(changeEditModalEstimateId(value.id));
                    dispatch(changeEditModalProjectId(value.projectId));
                }

                if (value.type === 'object' && modalMode === 'addNewTask') {
                    dispatch(changeEstimateId(null));
                    dispatch(changeProjectId(value.id));
                }

                if (value.type === 'object' && modalMode === 'editTask') {
                    // setProjectIdInEditMode(value.id);
                    dispatch(changeEditModalProjectId(value.id));
                    dispatch(changeEditModalEstimateId(null));
                }

                // console.log('inObjectsOrEstimates value: ', value);
                setFieldErrors((prev) => ({
                    ...prev,
                    objectOrEstimate: { isError: false, errorMessage: null },
                }));
            },
            [dispatch, setSelectedOption, modalMode]
        );

        return (
            <div className={cls.EstimateContainer}>
                <SearchSelectWithSubList
                    selected={selectedOption}
                    onChange={handleSelectChange}
                    placeholder={t(
                        'Features.ObjectsOrEstimatesSelect.SearchSelectWithSubList.placeholder.text'
                    )}
                    options={[
                        {
                            id: null,
                            name: t('Features.ObjectsOrEstimatesSelect.NotSelected.text'),
                            estimates: [],
                        },
                        ...projects,
                    ]}
                    IconAddon={
                        <span className={cls.IconWrapper}>
                            <Icon iconName="taskEstimate" />
                        </span>
                    }
                    isDisabled={isDisabled}
                    error={error.errorMessage}
                />
            </div>
        );
    }
);
